import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { LANGUAGE_EN, LANGUAGE_LABELS, LANGUAGE_ZH, translateUiText } from "../i18n/translations";

const I18nContext = createContext(null);

const STORAGE_KEY = "app_language";

const normalizeLanguage = (value) => (
  value === LANGUAGE_EN || value === "en" || value === "english" ? LANGUAGE_EN : LANGUAGE_ZH
);

export const I18nProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => normalizeLanguage(localStorage.getItem(STORAGE_KEY)));

  const setLanguage = useCallback((nextLanguage) => {
    const normalized = normalizeLanguage(nextLanguage);
    setLanguageState(normalized);
    localStorage.setItem(STORAGE_KEY, normalized);
    document.documentElement.lang = normalized;
    window.dispatchEvent(new CustomEvent("app-language-change", { detail: { language: normalized } }));
  }, []);

  const toggleLanguage = useCallback(() => {
    setLanguage(language === LANGUAGE_EN ? LANGUAGE_ZH : LANGUAGE_EN);
  }, [language, setLanguage]);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const value = useMemo(() => ({
    language,
    isEnglish: language === LANGUAGE_EN,
    languageLabel: LANGUAGE_LABELS[language] || LANGUAGE_LABELS[LANGUAGE_ZH],
    setLanguage,
    toggleLanguage,
    t: (text) => translateUiText(text, language),
  }), [language, setLanguage, toggleLanguage]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within I18nProvider");
  }
  return context;
};

